import React from 'react';

import { type HexAlpha, type UUID } from '@nfctron/nexus-core';
import { type ExtendedComponent, UiButton, useFormat } from '@nfctron/nexus-react';

import { Collapse } from '@mantine/core';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';

import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useTranslate } from '@root/lib/hooks/useTranslation';

import { CartStoreExtraProductsSlice, useCartStore } from '../../stores/cartStore';
import { ProductCardCarted } from '../Product/ProductCardCarted';
import { ProductCardProduct } from '../Product/ProductCardProduct';
import { ProductCardProductGroup } from '../Product/ProductCardProductGroup';

import { TicketCardDivider } from './TicketCardDivider';

/**
 * Event/Cart/TicketCardExtraProducts component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const TicketCardExtraProducts: React.FunctionComponent<TicketCardExtraProductsProps> = ({
	cartTicketId,
	dividerBgColor = '#FFF',
	...props
}) => {
	const { t } = useTranslate(APP_I18N_NAMESPACE.MAIN, TicketCardExtraProducts.displayName);
	const format = useFormat();
	const cartConfig = useCartStore((s) => s.cartConfig);
	const getCartedTicketTotal = useCartStore((state) => state.getCartedTicketTotal);
	const getProductControls = useCartStore((state) => state.getProductControls);
	const getExtraProductStateById = useCartStore((state) => state.getExtraProductStateById);
	const isExtraProductConfigurationRequired = useCartStore((state) => state.isExtraProductConfigurationRequired);
	const getExtraProductGroupProducts = useCartStore((state) => state.getExtraProductGroupProducts);
	const getExtraProductGroupStateById = useCartStore((state) => state.getExtraProductGroupStateById);

	const { ticket, ticketType } = useCartStore((state) => state.getCartedTicket({ cartTicketId }));

	/** available products list expanded state */
	const [isExpanded, setIsExpanded] = React.useState(false);

	/** get all renderable product control fields */
	const fields = React.useMemo(
		() =>
			getProductControls({ ticketTypeId: ticket.ticketTypeId })
				.filter((c) => {
					switch (c.type) {
						case CartStoreExtraProductsSlice.PRODUCT_CONTROL_TYPE.SIMPLE: {
							/** hide products unable to add to cart (limited/soldOut) */
							const meta = getExtraProductStateById({
								ticketProductId: c.ticketProductId,
								cartTicketId: ticket.cartTicketId,
							});
							if (meta.quantityLimited || meta.soldOut) return false;

							/** show complex products at all times */
							const isComplex = isExtraProductConfigurationRequired({
								ticketProductId: c.ticketProductId,
								ticketTypeId: ticket.ticketTypeId,
							});
							if (isComplex) return true;

							/** show only products that are not already carted */
							const isAlreadyCarted = ticket.extraProducts.find((ep) => ep.ticketProductId === c.ticketProductId);
							return !isAlreadyCarted;
						}
						case CartStoreExtraProductsSlice.PRODUCT_CONTROL_TYPE.GROUP: {
							/** hide when no products within the given group */
							const availableProducts = getExtraProductGroupProducts({
								productGroupId: c.productGroupId,
								ticketTypeId: ticket.ticketTypeId,
							});
							if (availableProducts.length === 0) return false;

							/** show only available product groups */
							const meta = getExtraProductGroupStateById({
								productGroupId: c.productGroupId,
								cartTicketId: ticket.cartTicketId,
							});
							return !meta.quantityLimited && !meta.soldOut;
						}
						default:
							return false;
					}
				})
				.map((control) => {
					switch (control.type) {
						case CartStoreExtraProductsSlice.PRODUCT_CONTROL_TYPE.SIMPLE: {
							return <ProductCardProduct key={control.ticketProductId} cartTicketId={cartTicketId} ticketProductId={control.ticketProductId} />;
						}
						case CartStoreExtraProductsSlice.PRODUCT_CONTROL_TYPE.GROUP: {
							return <ProductCardProductGroup key={control.productGroupId} cartTicketId={cartTicketId} productGroupId={control.productGroupId} />;
						}
						default:
							return null;
					}
				}),
		[
			cartTicketId,
			getExtraProductGroupProducts,
			getExtraProductGroupStateById,
			getExtraProductStateById,
			getProductControls,
			isExtraProductConfigurationRequired,
			ticket.cartTicketId,
			ticket.extraProducts,
			ticket.ticketTypeId,
		],
	);

	return (
		<div className="rounded-xl border border-gray-200 bg-gray-50 pb-2" {...props}>
			{/* ticket header */}
			<header className="flex items-end justify-between gap-3 p-4 pb-2">
				<div>
					{/* ticket type name */}
					<span className="block text-base text-zinc-500">{ticketType.namePub}</span>
					{/* ticket holder name */}
					<span className="text-lucky-point-500 block text-xl font-bold">{ticket.holderName}</span>
				</div>
				{/* ticket type price */}
				<span className="text-lucky-point-500 block text-xl">
					{format.price(getCartedTicketTotal({ cartTicketId: ticket.cartTicketId }).total, cartConfig.currencyId)}
				</span>
			</header>

			{/* added extra products to the ticket */}
			{ticket.extraProducts.length > 0 && (
				<React.Fragment>
					{/* divider */}
					<TicketCardDivider bgColor={dividerBgColor}>{t('cartedProductsDivider.text')}</TicketCardDivider>

					{/* carted products */}
					<div className="px-4 py-2">
						<div className="flex flex-col gap-3">
							{ticket.extraProducts.map((extraProduct) => (
								<ProductCardCarted key={extraProduct.cartProductId} cartTicketId={cartTicketId} cartProductId={extraProduct.cartProductId} />
							))}
						</div>
					</div>
				</React.Fragment>
			)}

			{/* product field controls */}
			{fields.length > 0 && (
				<React.Fragment>
					{/* divider */}
					<TicketCardDivider bgColor={dividerBgColor}>{t('availableProductsDivider.text')}</TicketCardDivider>

					{/* available products */}
					<div className="grid grid-cols-1 gap-3 px-4 py-2">
						{/* featured products (top 3) */}
						{fields.slice(0, 3).map((field) => field)}
						{/* other products */}
						{fields.length > 3 && (
							<React.Fragment>
								<Collapse in={isExpanded}>
									<div className="grid grid-cols-1 gap-3">{fields.slice(3).map((field) => field)}</div>
								</Collapse>
								<UiButton
									variant="subtle"
									fullWidth
									leftSection={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
									onClick={() => setIsExpanded((e) => !e)}
								>
									{isExpanded ? t('collapseProductsButton.text') : t('expandProductsButton.text')}
								</UiButton>
							</React.Fragment>
						)}
					</div>
				</React.Fragment>
			)}
		</div>
	);
};

/**
 * Event/Cart/TicketCardExtraProducts component props
 * @export
 */
export type TicketCardExtraProductsProps = ExtendedComponent<{
	cartTicketId: UUID;
	dividerBgColor?: HexAlpha;
}>;

TicketCardExtraProducts.displayName = 'Event/Cart/TicketCardExtraProducts';
